import { useMemo, useRef } from "react";
import { BaseChartColors } from "../../../../components/charts/constants";
import { ChartWrapper } from "../../../../components/ChartWrapper";
import { useWorkspaceStore } from "../../../../store/workspace/WorkspaceStore";
import { determineChartContent, hasNoData } from "../../utils";
import { useGetSpendByEmployeeRangeData } from "./hooks/useGetSpendByEmployeeRangeData";
import { mapSpendBreakdownResultToChartData } from "./utils";
import { BarAndLineChart } from "../../../../components/charts/BarAndLineChart/BarAndLineChart";
import { DEFAULT_EMPLOYEE_RANGES } from "../../../../utils/employeeRanges";
import * as am4charts from "@amcharts/amcharts4/charts";
import { BarSize } from "../../../../components/charts/BarAndLineChart/types";
import { useCombinedFilters } from "../../../../hooks/useCombinedFilters";
import { CHART_CONSTANTS } from "../constants";

const { baseId, chartTitle, tooltip, csvFieldMappings } =
  CHART_CONSTANTS.marketSizeByEmployee;

interface MarketSizeByEmployeeChartProps {
  barSize?: BarSize;
  applyWorkspaceFiltersOnly?: boolean;
}

export function MarketSizeByEmployeeChart({
  barSize,
  applyWorkspaceFiltersOnly = false,
}: MarketSizeByEmployeeChartProps = {}) {
  const combinedFilters = useCombinedFilters(applyWorkspaceFiltersOnly);
  const spendCategoryWeights = useWorkspaceStore(
    (state) => state.draft?.configuration.tam.spendCategoryWeights
  );
  const localizeInstalls = useWorkspaceStore(
    (state) => state.draft?.configuration?.localizeInstalls ?? true
  );
  const chartRef = useRef<am4charts.XYChart>(null);
  const { isLoading, spendBreakdownResults } = useGetSpendByEmployeeRangeData({
    companyFilters: combinedFilters || {},
    customSpendCategory: spendCategoryWeights && {
      name: "",
      categories: spendCategoryWeights,
    },
    employeeRanges: DEFAULT_EMPLOYEE_RANGES,
    localizeInstalls,
  });

  const chartData = useMemo(
    () => mapSpendBreakdownResultToChartData(spendBreakdownResults).reverse(),
    [spendBreakdownResults]
  );

  const noDataReturned = useMemo(
    () => hasNoData(isLoading, chartData),
    [chartData, isLoading]
  );

  const chartContent = useMemo(() => {
    return determineChartContent({
      isLoading,
      noDataReturned,
      baseId,
      chartToRender: (
        <BarAndLineChart
          id={`${baseId}-chart`}
          chartData={chartData}
          barSeriesColor={BaseChartColors.Pink}
          xAxisLabel="Spend (USD)"
          barSeriesName="SPEND (USD)"
          lineSeriesName="NO. OF COMPANIES"
          ref={chartRef}
          barSize={barSize}
        />
      ),
    });
  }, [chartData, isLoading, noDataReturned, barSize]);

  return (
    <ChartWrapper
      id={`${baseId}-chart-wrapper`}
      title={chartTitle}
      tooltipTitle={tooltip}
      chart={chartContent}
      noData={noDataReturned}
      isLoading={isLoading}
      chartRef={chartRef}
      csvFieldMappings={csvFieldMappings}
    />
  );
}
